<template>
  <div class="debt-providers-table">
    <b-table
      :items="tableData"
      :fields="tableFields"
      :busy="isLoading"
      class="debt-providers-table__table table-bordered table-hover"
      role="table"
      :aria-label="tableAriaLabel"
    >
      <!-- Custom table header -->
      <template slot="thead-top">
        <tr role="row">
          <th class="debt-providers-table__header text-center" role="columnheader">STT</th>
          <th class="debt-providers-table__header text-center" role="columnheader">Nhà cung cấp</th>
          <th class="debt-providers-table__header text-center" colspan="2" role="columnheader">
            Phát sinh trong kì
          </th>
          <th class="debt-providers-table__header text-center" colspan="2" role="columnheader">
            Số dư cuối kì
          </th>
          <th
            class="debt-providers-table__header text-center"
            v-if="canInsertTransaction"
            role="columnheader"
            :aria-label="actionsColumnLabel"
          >
            <i class="fa fa-cogs" aria-hidden="true" />
          </th>
        </tr>
      </template>

      <!-- Loading state -->
      <template v-slot:table-busy>
        <vcl-table :speed="5" :animate="true" :columns="10" />
      </template>

      <!-- Row index column -->
      <template v-slot:cell(index)="row">
        <span :aria-label="`Hàng số ${row.index + 1 + offsetItems}`">
          {{ row.index + 1 + offsetItems }}
        </span>
      </template>

      <!-- Provider name column with link -->
      <template v-slot:cell(name)="row">
        <a
          class="debt-providers-table__provider-link font-weight-bold"
          :href="`#/customers/update-customer?id=${row.item.id}`"
          target="_blank"
          :aria-label="`Xem chi tiết nhà cung cấp ${row.item.name}`"
        >
          <span class="debt-providers-table__provider-name">
            {{ `NCC.${row.item.name}` }}
          </span>
          <span class="debt-providers-table__provider-phone">
            {{ `ĐT: ${row.item.phoneNo}` }}
          </span>
        </a>
      </template>

      <!-- Paid amount column -->
      <template v-slot:cell(paidAmount)="row">
        <span :aria-label="`Số tiền đã trả: ${formatCurrency(row.item.paidAmount)}`">
          {{ formatCurrency(row.item.paidAmount) }}
        </span>
      </template>

      <!-- Amount column -->
      <template v-slot:cell(amount)="row">
        <span :aria-label="`Tổng số tiền: ${formatCurrency(row.item.amount)}`">
          {{ formatCurrency(row.item.amount) }}
        </span>
      </template>

      <!-- Debt sum column (when paid > amount) -->
      <template v-slot:cell(debtSum)="row">
        <span 
          v-if="row.item.paidAmount > row.item.amount"
          :aria-label="`Số nợ phải thu: ${formatCurrency(row.item.paidAmount - row.item.amount)}`"
        >
          {{ formatCurrency(row.item.paidAmount - row.item.amount) }}
        </span>
      </template>

      <!-- Paid sum column (when amount > paid) -->
      <template v-slot:cell(paidSum)="row">
        <span 
          v-if="row.item.amount > row.item.paidAmount"
          :aria-label="`Số tiền phải trả: ${formatCurrency(row.item.amount - row.item.paidAmount)}`"
        >
          {{ formatCurrency(row.item.amount - row.item.paidAmount) }}
        </span>
      </template>

      <!-- Actions column -->
      <template v-slot:cell(actions)="row">
        <DebtProvidersActions
          :item="row.item"
          @view-detail="onViewDetail"
          @create-cash-transaction="onCreateCashTransaction"
          @create-bank-transaction="onCreateBankTransaction"
        />
      </template>

      <!-- Summary footer row -->
      <template slot="bottom-row">
        <td colspan="2" class="debt-providers-table__summary-label text-right font-weight-bolder">
          <span>Tổng</span>
        </td>
        <td class="debt-providers-table__summary-value text-right font-weight-bolder">
          <span :aria-label="`Tổng số tiền đã trả: ${formatCurrency(totalPaidAmount)}`">
            {{ formatCurrency(totalPaidAmount) }}
          </span>
        </td>
        <td class="debt-providers-table__summary-value text-right font-weight-bolder">
          <span :aria-label="`Tổng số tiền: ${formatCurrency(totalAmount)}`">
            {{ formatCurrency(totalAmount) }}
          </span>
        </td>
        <td class="debt-providers-table__summary-value text-right font-weight-bolder">
          <span :aria-label="`Tổng nợ phải thu: ${formatCurrency(totalDebtSum)}`">
            {{ formatCurrency(totalDebtSum) }}
          </span>
        </td>
        <td class="debt-providers-table__summary-value text-right font-weight-bolder">
          <span :aria-label="`Tổng tiền phải trả: ${formatCurrency(totalPaidSum)}`">
            {{ formatCurrency(totalPaidSum) }}
          </span>
        </td>
        <td class="debt-providers-table__summary-value" v-if="canInsertTransaction"></td>
      </template>
    </b-table>
  </div>
</template>

<script>
import { VclTable } from 'vue-content-loading';
import { convertPrice } from '@/utils/common';
import DebtProvidersActions from './DebtProvidersActions.vue';

export default {
  name: 'DebtProvidersTable',
  components: {
    VclTable,
    DebtProvidersActions,
  },
  props: {
    /**
     * Table data array
     */
    data: {
      type: Array,
      default: () => [],
      validator: (data) => Array.isArray(data),
    },
    /**
     * Loading state
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
    /**
     * Current page number for offset calculation
     */
    currentPage: {
      type: Number,
      default: 1,
      validator: (page) => page > 0,
    },
    /**
     * Items per page for offset calculation
     */
    pageSize: {
      type: Number,
      default: 10,
      validator: (size) => size > 0,
    },
    /**
     * Permission to insert transactions
     */
    canInsertTransaction: {
      type: Boolean,
      default: false,
    },
    /**
     * Total amounts for summary calculations
     */
    totals: {
      type: Object,
      default: () => ({
        totalPaidAmount: 0,
        totalAmount: 0,
        totalDebtSum: 0,
        totalPaidSum: 0,
      }),
    },
  },
  computed: {
    /**
     * Table data with proper structure
     */
    tableData() {
      return this.data || [];
    },

    /**
     * Table field configuration
     */
    tableFields() {
      const commonStyle = {
        textAlign: 'center',
        fontWeight: 600,
        color: '#181c32',
        width: '5%',
        whiteSpace: 'break-spaces',
      };

      return [
        {
          key: 'index',
          label: '[1]',
          sortable: false,
          thStyle: { ...commonStyle, width: '5%' },
          tdClass: 'text-center',
        },
        {
          key: 'name',
          label: '[2]',
          sortable: false,
          thStyle: { ...commonStyle, width: 'calc(35% - 50px)' },
        },
        {
          key: 'paidAmount',
          label: 'Nợ\n[3]',
          sortable: false,
          thStyle: { ...commonStyle, width: '15%' },
          tdClass: 'text-right break-line',
        },
        {
          key: 'amount',
          label: 'Có\n[4]',
          sortable: false,
          thStyle: { ...commonStyle, width: '15%' },
          tdClass: 'text-right break-line',
        },
        {
          key: 'debtSum',
          label: 'Nợ [Phải thu]\n= 3 - 4',
          sortable: false,
          thStyle: { ...commonStyle, width: '15%' },
          tdClass: 'text-right break-line',
        },
        {
          key: 'paidSum',
          label: 'Có [Phải trả]\n= 4 - 3',
          sortable: false,
          thStyle: { ...commonStyle, width: '15%' },
          tdClass: 'text-right break-line',
        },
        {
          key: this.canInsertTransaction ? 'actions' : '',
          label: '',
          sortable: false,
          thStyle: { ...commonStyle, width: '50px' },
        },
      ];
    },

    /**
     * Calculate offset for row numbering
     */
    offsetItems() {
      return (this.currentPage - 1) * this.pageSize;
    },

    /**
     * Accessibility labels
     */
    tableAriaLabel() {
      return 'Bảng công nợ nhà cung cấp';
    },

    actionsColumnLabel() {
      return 'Các hành động có thể thực hiện';
    },

    // Summary totals
    totalPaidAmount() {
      return this.totals.totalPaidAmount || 0;
    },

    totalAmount() {
      return this.totals.totalAmount || 0;
    },

    totalDebtSum() {
      return this.totals.totalDebtSum || 0;
    },

    totalPaidSum() {
      return this.totals.totalPaidSum || 0;
    },
  },
  methods: {
    /**
     * Format currency values
     */
    formatCurrency(value) {
      return convertPrice(value);
    },

    /**
     * Handle view detail action
     */
    onViewDetail(item) {
      this.$emit('view-detail', item);
    },

    /**
     * Handle create cash transaction action
     */
    onCreateCashTransaction(item) {
      this.$emit('create-cash-transaction', item);
    },

    /**
     * Handle create bank transaction action
     */
    onCreateBankTransaction(item) {
      this.$emit('create-bank-transaction', item);
    },
  },
};
</script>

<style lang="scss" scoped>
.debt-providers-table {
  &__table {
    margin-bottom: 0;
  }

  &__header {
    text-align: center;
    font-weight: 600;
    color: #181c32;
    white-space: break-spaces;
  }

  &__provider-link {
    color: unset;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline !important;
    }
  }

  &__provider-name {
    display: block;
  }

  &__provider-phone {
    display: block;
    font-size: 0.875rem;
    color: #6c757d;
  }

  &__summary-label,
  &__summary-value {
    background-color: #f8f9fa;
    border-top: 2px solid #dee2e6;
  }

  // Responsive design
  @media (max-width: 768px) {
    &__table {
      font-size: 0.875rem;
    }

    &__provider-name,
    &__provider-phone {
      font-size: 0.8rem;
    }
  }
}

// Global styles for table content
:deep(.break-line) {
  white-space: break-spaces;
}
</style>
